import { useCallback, useEffect, useMemo, useState } from "react";
import { FolderOpen, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  fetchMaterialidadConfig,
  saveMaterialidadConfig,
} from "@/modules/tramites/materialidad/materialidadApi";

/**
 * Configuración por empresa — panel reutilizable (separable de Trámites).
 */
export default function MaterialidadConfigPanel({ clientId, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [config, setConfig] = useState(null);
  const [meta, setMeta] = useState(null);

  const loadConfig = useCallback(async () => {
    if (!clientId) {
      setCatalog(null);
      setConfig(null);
      setMeta(null);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchMaterialidadConfig(clientId);
      setCatalog(data.catalog || null);
      setConfig(data.config || null);
      setMeta(data.meta || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cargar la configuración de materialidad.");
      setCatalog(null);
      setConfig(null);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const expedienteTypes = catalog?.expediente_types || [];
  const evidenceCategories = catalog?.evidence_categories || [];
  const operationTemplates = catalog?.operation_type_templates || [];
  const enabledSet = useMemo(
    () => new Set(config?.enabled_evidence_categories || []),
    [config?.enabled_evidence_categories],
  );

  const patchConfig = (patch) => {
    setConfig((prev) => ({ ...(prev || {}), ...patch }));
  };

  const applyOperationTemplate = (templateId) => {
    const tpl = operationTemplates.find((t) => t.id === templateId);
    if (!tpl) {
      patchConfig({ operation_type: templateId });
      return;
    }
    const valid = new Set(evidenceCategories.map((c) => c.id));
    const suggested = (tpl.suggested_categories || []).filter((id) => valid.has(id));
    patchConfig({
      operation_type: templateId,
      enabled_evidence_categories: suggested.length
        ? suggested
        : config?.enabled_evidence_categories || [],
    });
    toast.message(`Plantilla «${tpl.label}» aplicada a la lista de verificación.`);
  };

  const toggleCategory = (categoryId, checked) => {
    const current = new Set(config?.enabled_evidence_categories || []);
    if (checked) current.add(categoryId);
    else current.delete(categoryId);
    patchConfig({ enabled_evidence_categories: Array.from(current) });
  };

  const handleSave = async () => {
    if (!clientId || !config) return;
    setSaving(true);
    try {
      // No tocar redacción de cartas desde este panel (pestaña dedicada).
      const {
        carta_cliente_intro: _a,
        carta_cliente_providers_lead: _b,
        carta_cliente_legal_note: _c,
        carta_proveedor_intro: _d,
        carta_proveedor_footer: _e,
        ...configWithoutLetters
      } = config;
      const data = await saveMaterialidadConfig(clientId, configWithoutLetters);
      setCatalog(data.catalog || catalog);
      setConfig(data.config || config);
      setMeta(data.meta || null);
      toast.success("Configuración de materialidad guardada.");
      onSaved?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar la configuración.");
    } finally {
      setSaving(false);
    }
  };

  if (!clientId) {
    return <p className="text-sm text-slate-600 m-0">Seleccione una empresa para configurar materialidad.</p>;
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-600">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Cargando configuración…
      </div>
    );
  }

  return (
    <section
      className="rounded-md border border-slate-200 bg-white shadow-sm p-6 space-y-5"
      aria-label="Configuración de materialidad"
      data-testid="materialidad-config-section"
    >
      <div className="flex items-start gap-3">
        <div className="rounded-md bg-[#0F2D5C]/10 p-2">
          <FolderOpen className="w-5 h-5 text-[#0F2D5C]" strokeWidth={1.8} />
        </div>
        <div>
          <h2 className="text-sm font-bold text-[#0F2D5C] m-0">Configuración básica</h2>
          <p className="text-xs text-slate-600 mt-1 m-0">
            {meta?.configured
              ? `Última actualización: ${meta.updated_at ? new Date(meta.updated_at).toLocaleString("es-MX") : "—"}`
              : "Sin guardar aún — se usarán valores por defecto hasta que pulse Guardar."}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="materialidad-expediente-type">Tipo de expediente</Label>
        <Select
          value={config?.expediente_type || "mixto"}
          onValueChange={(value) => patchConfig({ expediente_type: value })}
        >
          <SelectTrigger id="materialidad-expediente-type" className="rounded-sm max-w-md">
            <SelectValue placeholder="Seleccione tipo" />
          </SelectTrigger>
          <SelectContent>
            {expedienteTypes.map((row) => (
              <SelectItem key={row.id} value={row.id}>
                {row.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="materialidad-operation-type">Plantilla por tipo de operación</Label>
        <Select
          value={config?.operation_type || "general"}
          onValueChange={applyOperationTemplate}
        >
          <SelectTrigger
            id="materialidad-operation-type"
            className="rounded-sm max-w-md"
            data-testid="materialidad-operation-type"
          >
            <SelectValue placeholder="Seleccione plantilla" />
          </SelectTrigger>
          <SelectContent>
            {operationTemplates.map((row) => (
              <SelectItem key={row.id} value={row.id}>
                {row.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-[11px] text-slate-500 m-0">
          Al cambiar la plantilla se sugieren categorías de la lista de verificación. Puede ajustarlas después; no bloquea operaciones sin contrato.
        </p>
      </div>

      <div className="space-y-3">
        <Label>Categorías de evidencia activas (lista de verificación del periodo)</Label>
        <div className="grid gap-2 sm:grid-cols-2">
          {evidenceCategories.map((cat) => (
            <label
              key={cat.id}
              className="flex items-start gap-2 rounded-sm border border-slate-200 px-3 py-2 text-sm cursor-pointer hover:border-[#0F2D5C]/30"
            >
              <Checkbox
                checked={enabledSet.has(cat.id)}
                onCheckedChange={(checked) => toggleCategory(cat.id, Boolean(checked))}
                data-testid={`materialidad-cat-${cat.id}`}
              />
              <span>
                {cat.label}
                <span className="block text-[10px] text-slate-500 mt-0.5">Origen: {cat.source_hint}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-sm border border-slate-100 bg-slate-50/60 px-3 py-2">
        <div>
          <p className="text-sm font-medium text-slate-800 m-0">Exigir vínculo UUID en CFDI</p>
          <p className="text-[11px] text-slate-500 m-0">Cada evidencia fiscal deberá referenciar un UUID del Visor.</p>
        </div>
        <Switch
          checked={Boolean(config?.require_cfdi_uuid_link)}
          onCheckedChange={(checked) => patchConfig({ require_cfdi_uuid_link: checked })}
          data-testid="materialidad-require-uuid"
        />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-sm border border-slate-100 bg-slate-50/60 px-3 py-2">
        <div>
          <p className="text-sm font-medium text-slate-800 m-0">Sugerir evidencia desde CFDI cargados</p>
          <p className="text-[11px] text-slate-500 m-0">Detección automática desde CFDI cargados y contabilidad.</p>
        </div>
        <Switch
          checked={Boolean(config?.auto_suggest_from_cfdi)}
          onCheckedChange={(checked) => patchConfig({ auto_suggest_from_cfdi: checked })}
          data-testid="materialidad-auto-suggest"
        />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-sm border border-slate-100 bg-slate-50/60 px-3 py-2">
        <div>
          <p className="text-sm font-medium text-slate-800 m-0">Solo CFDI contabilizados</p>
          <p className="text-[11px] text-slate-500 m-0">Omite alertas de materialidad si el UUID no está en Excel/confronta del periodo.</p>
        </div>
        <Switch
          checked={config?.filter_unaccounted_cfdi !== false}
          onCheckedChange={(checked) => patchConfig({ filter_unaccounted_cfdi: checked })}
          data-testid="materialidad-filter-unaccounted"
        />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-sm border border-slate-100 bg-slate-50/60 px-3 py-2">
        <div>
          <p className="text-sm font-medium text-slate-800 m-0">Detectar giro inconsistente (emitidos y compras)</p>
          <p className="text-[11px] text-slate-500 m-0">
            Compara conceptos contra actividades de la CSF. Emitidos: alerta alta. Compras atípicas con monto relevante: señal suave.
          </p>
        </div>
        <Switch
          checked={config?.detect_giro_mismatch !== false}
          onCheckedChange={(checked) => patchConfig({ detect_giro_mismatch: checked })}
          data-testid="materialidad-detect-giro"
        />
      </div>

      <div
        className="space-y-3 rounded-sm border border-slate-200 bg-white px-3 py-3"
        data-testid="materialidad-umbral-section"
      >
        <div>
          <p className="text-sm font-medium text-slate-800 m-0">Umbral de importe alto (materialidad)</p>
          <p className="text-[11px] text-slate-500 m-0">
            Control interno del despacho (no es un % oficial del SAT). Modo relativo:
            max(piso, min(techo, % × ingresos emitidos del mes)). Sin ingresos → umbral fijo/piso.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label className="text-xs">Modo</Label>
            <Select
              value={config?.importe_alto_mode || "relative"}
              onValueChange={(v) => patchConfig({ importe_alto_mode: v })}
            >
              <SelectTrigger className="rounded-sm h-9 text-sm" data-testid="materialidad-umbral-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="relative">Relativo a ingresos del mes</SelectItem>
                <SelectItem value="fixed">Fijo (MXN)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">% sobre ingresos del mes</Label>
            <input
              type="number"
              min={0}
              max={100}
              step={0.1}
              className="flex h-9 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
              value={config?.importe_alto_pct_ingresos_mes ?? 2}
              onChange={(e) => patchConfig({ importe_alto_pct_ingresos_mes: Number(e.target.value) })}
              data-testid="materialidad-umbral-pct"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Piso (MXN)</Label>
            <input
              type="number"
              min={0}
              step={1000}
              className="flex h-9 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
              value={config?.importe_alto_piso_mxn ?? 50000}
              onChange={(e) => patchConfig({ importe_alto_piso_mxn: Number(e.target.value) })}
              data-testid="materialidad-umbral-piso"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Techo (MXN)</Label>
            <input
              type="number"
              min={0}
              step={1000}
              className="flex h-9 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
              value={config?.importe_alto_techo_mxn ?? 500000}
              onChange={(e) => patchConfig({ importe_alto_techo_mxn: Number(e.target.value) })}
              data-testid="materialidad-umbral-techo"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Umbral fijo / fallback (MXN)</Label>
            <input
              type="number"
              min={0}
              step={1000}
              className="flex h-9 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
              value={config?.importe_alto_fijo_mxn ?? 50000}
              onChange={(e) => patchConfig({ importe_alto_fijo_mxn: Number(e.target.value) })}
              data-testid="materialidad-umbral-fijo"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Piso intangibles (MXN)</Label>
            <input
              type="number"
              min={0}
              step={1000}
              className="flex h-9 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
              value={config?.intangible_piso_mxn ?? 30000}
              onChange={(e) => patchConfig({ intangible_piso_mxn: Number(e.target.value) })}
              disabled={config?.detect_intangibles_alto === false}
              data-testid="materialidad-umbral-intangible"
            />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-sm border border-slate-100 bg-slate-50/60 px-3 py-2">
          <div>
            <p className="text-sm font-medium text-slate-800 m-0">Umbral más sensible en servicios intangibles</p>
            <p className="text-[11px] text-slate-500 m-0">
              Asesorías, honorarios, marketing, software, etc. suelen exigir más evidencia aunque el monto sea menor.
            </p>
          </div>
          <Switch
            checked={config?.detect_intangibles_alto !== false}
            onCheckedChange={(checked) => patchConfig({ detect_intangibles_alto: checked })}
            data-testid="materialidad-detect-intangibles"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="materialidad-notes">Notas internas</Label>
        <Textarea
          id="materialidad-notes"
          value={config?.notes || ""}
          onChange={(e) => patchConfig({ notes: e.target.value })}
          placeholder="Instrucciones del despacho para este expediente…"
          className="rounded-sm min-h-[80px] text-sm"
        />
      </div>

      <Button
        type="button"
        className="rounded-sm bg-[#0F2D5C] hover:bg-[#0F2D5C]/90"
        disabled={saving || !config}
        onClick={handleSave}
        data-testid="materialidad-save-btn"
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
        Guardar configuración
      </Button>
    </section>
  );
}
